/**
 * One broker-action resolver (SYNC-20).
 *
 * Every raw action string — a file-import broker table entry, a SnapTrade
 * activity type, a legacy alias, or an already-canonical TransactionType —
 * resolves here. Adding a broker action is a one-line change in the broker's
 * table (vanguard.ts / schwab.ts) or in SNAPTRADE_TYPE_MAP below.
 *
 * Where a type is ambiguous about direction (SnapTrade `TRANSFER`), the sign
 * of units, then amount, decides; with neither the result is null — never a
 * guess.
 */

import { TransactionType } from '../../enums/TransactionType.js';
import { SCHWAB_ACTION_MAP } from './schwab.js';
import { VANGUARD_ACTION_MAP } from './vanguard.js';

export interface ActionContext {
  /** 'snaptrade' selects SnapTrade type codes; a file broker selects its table first. */
  broker?: string;
  unitsSign?: number | null;
  amountSign?: number | null;
}

const CANONICAL: ReadonlySet<string> = new Set(Object.values(TransactionType));

/** SnapTrade activity `type` codes (upper-case). */
export const SNAPTRADE_TYPE_MAP: Readonly<Record<string, TransactionType>> = {
  BUY: TransactionType.Buy,
  SELL: TransactionType.Sell,
  DIVIDEND: TransactionType.Dividend,
  CONTRIBUTION: TransactionType.Deposit,
  WITHDRAWAL: TransactionType.Withdrawal,
  REI: TransactionType.Reinvestment, // Dividend reinvestment
  INTEREST: TransactionType.Interest,
  'CREDIT INTEREST': TransactionType.Interest,
  'BOND INTEREST': TransactionType.Interest,
  FEE: TransactionType.Fee,
  TAX: TransactionType.Fee, // Tax withholding, booked as a fee for now
  SPLIT: TransactionType.Split,
  STOCK_DIVIDEND: TransactionType.Dividend,
  OPTIONEXPIRATION: TransactionType.OptionExpiration,
  OPTIONASSIGNMENT: TransactionType.OptionAssignment,
  OPTIONEXERCISE: TransactionType.OptionExercise,
  // Raw brokerage values SnapTrade doesn't always normalize
  EXPIRED: TransactionType.OptionExpiration,
  ASSIGNMENT: TransactionType.OptionAssignment,
  EXERCISE: TransactionType.OptionExercise,
  JOURNAL: TransactionType.Journal,
  CASH_ADJUSTMENT: TransactionType.CashAdjustment,
  'REDEMPTION ADJ': TransactionType.Redemption, // Bond redemption - removes position
  'CXL REDEMPTION ADJ': TransactionType.RedemptionCancel, // Cash proceeds from bond redemption
};

/** Aliases seen in files, older parsers and internal option names (lower_snake). */
const ALIASES: Readonly<Record<string, TransactionType>> = {
  bought: TransactionType.Buy,
  purchase: TransactionType.Buy,
  sold: TransactionType.Sell,
  sale: TransactionType.Sell,
  option_open_sell: TransactionType.SellToOpen,
  option_open_buy: TransactionType.BuyToOpen,
  option_close_sell: TransactionType.SellToClose,
  option_close_buy: TransactionType.BuyToClose,
  selltoopen: TransactionType.SellToOpen,
  buytoopen: TransactionType.BuyToOpen,
  selltoclose: TransactionType.SellToClose,
  buytoclose: TransactionType.BuyToClose,
  option_expire: TransactionType.OptionExpiration,
  optionexpiration: TransactionType.OptionExpiration,
  expired: TransactionType.OptionExpiration,
  optionassignment: TransactionType.OptionAssignment,
  assigned: TransactionType.OptionAssignment,
  assignment: TransactionType.OptionAssignment,
  optionexercise: TransactionType.OptionExercise,
  exercised: TransactionType.OptionExercise,
  exercise: TransactionType.OptionExercise,
  qualified_dividend: TransactionType.Dividend,
  non_qualified_dividend: TransactionType.Dividend,
  bond_interest: TransactionType.Interest,
  credit_interest: TransactionType.Interest,
  reinvest_dividend: TransactionType.Reinvestment,
  reinvest: TransactionType.Reinvestment,
  contribution: TransactionType.Deposit,
  cash_contribution: TransactionType.Deposit,
  funds_received: TransactionType.Deposit,
  stock_split: TransactionType.Split,
  spin_off: TransactionType.Spinoff,
  commission: TransactionType.Fee,
  tax: TransactionType.Fee,
  adjustment: TransactionType.CashAdjustment,
  money_market_purchase: TransactionType.SweepIn,
  money_market_redemption: TransactionType.SweepOut,
  paid_lending_interest: TransactionType.FplIncome,
  fully_paid_lending_income: TransactionType.FplIncome,
};

function bySign(ctx: ActionContext, positive: TransactionType, negative: TransactionType): TransactionType | null {
  const sign = Math.sign(ctx.unitsSign || 0) || Math.sign(ctx.amountSign || 0);
  if (sign > 0) return positive;
  if (sign < 0) return negative;
  return null;
}

/** Keyword fallback for free-text actions; direction words are matched whole. */
function byKeyword(lower: string, ctx: ActionContext): TransactionType | null {
  const has = (re: RegExp) => re.test(lower);
  if (has(/assign/)) return TransactionType.OptionAssignment;
  if (has(/exercis/)) return TransactionType.OptionExercise;
  if (has(/expir/)) return TransactionType.OptionExpiration;
  if (has(/redemption/)) return TransactionType.Redemption;
  if (has(/merger/)) return TransactionType.Merger;
  if (has(/split/)) return TransactionType.Split;
  if (has(/spin[\s_-]?off/)) return TransactionType.Spinoff;
  if (has(/transfer/)) {
    if (has(/\b(in|incoming|received)\b/)) return TransactionType.TransferIn;
    if (has(/\b(out|outgoing|sent)\b/)) return TransactionType.TransferOut;
    return bySign(ctx, TransactionType.TransferIn, TransactionType.TransferOut);
  }
  if (has(/deposit/)) return TransactionType.Deposit;
  if (has(/withdrawal/)) return TransactionType.Withdrawal;
  if (has(/sweep/)) {
    if (has(/\b(in|into)\b/)) return TransactionType.SweepIn;
    if (has(/\b(out|out of)\b/)) return TransactionType.SweepOut;
    return null;
  }
  if (has(/\bfees?\b/)) return TransactionType.Fee;
  if (has(/adjustment/)) return TransactionType.CashAdjustment;
  if (has(/journal/)) return TransactionType.Journal;
  return null;
}

/**
 * Resolve a raw action to a TransactionType, or null when it can't be
 * resolved without guessing.
 */
export function resolveTransactionType(raw: string | null | undefined, ctx: ActionContext = {}): TransactionType | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  if (ctx.broker === 'snaptrade') {
    const upper = trimmed.toUpperCase();
    if (upper === 'TRANSFER') return bySign(ctx, TransactionType.TransferIn, TransactionType.TransferOut);
    return SNAPTRADE_TYPE_MAP[upper] ?? null;
  }

  const lower = trimmed.toLowerCase();
  const broker = (ctx.broker || '').toLowerCase();
  const brokerTable = broker.includes('schwab') ? SCHWAB_ACTION_MAP : broker.includes('vanguard') ? VANGUARD_ACTION_MAP : undefined;
  const fromBroker = brokerTable?.[lower];
  if (fromBroker) return fromBroker;

  // camelCase enum spellings ('sellToOpen') → snake_case
  const snake = trimmed.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase().replace(/[\s-]+/g, '_');
  if (CANONICAL.has(snake)) return snake as TransactionType;

  // Any broker's table before generic aliases: broker phrases are specific
  // (Schwab "Reinvest Dividend" is the dividend leg, not the purchase).
  const fromAnyBroker = VANGUARD_ACTION_MAP[lower] ?? SCHWAB_ACTION_MAP[lower];
  if (fromAnyBroker) return fromAnyBroker;

  const alias = ALIASES[snake];
  if (alias) return alias;

  return byKeyword(lower, ctx);
}

/** Canonical option actions, for callers that only care about option legs. */
export const OPTION_ACTION_TYPES: ReadonlySet<TransactionType> = new Set([
  TransactionType.BuyToOpen,
  TransactionType.BuyToClose,
  TransactionType.SellToOpen,
  TransactionType.SellToClose,
  TransactionType.OptionExpiration,
  TransactionType.OptionAssignment,
  TransactionType.OptionExercise,
]);
